import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import communityAPI from '../../../api/community';
import { useCommunityAuth } from '../../../contexts/CommunityAuthContext';
import { InlineSpinner } from '../../../componentes/LoadingIndicator';

export default function VerifyOTP() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    email,
    username,
    fromLogin,
    pendingAvatarDataUrl,
    pendingAvatarName,
    pendingCoverDataUrl,
    pendingCoverName
  } = location.state || {};
  const { login: persistUser } = useCommunityAuth();

  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (!username || !email) {
      navigate(fromLogin ? '/community/login' : '/community/register');
      return;
    }

    // Auto-resend OTP for users coming from login to ensure fresh code
    if (fromLogin) {
      const autoResend = async () => {
        try {
          await communityAPI.auth.resendOTP(username);
          setResendTimer(60);
        } catch (err) {
          // Silently fail - user can manually resend if needed
          console.log('Auto-resend failed:', err.response?.data?.error);
        }
      };
      autoResend();
    }
  }, [username, email, navigate, fromLogin]);

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    setError('');

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (!/^\d+$/.test(pastedData)) return;

    const newOtp = [...otp];
    pastedData.split('').forEach((char, i) => {
      if (i < 6) newOtp[i] = char;
    });
    setOtp(newOtp);

    const nextIndex = Math.min(pastedData.length, 5);
    inputRefs.current[nextIndex]?.focus();
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    const otpCode = otp.join('');

    if (otpCode.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }

    setError('');
    setIsVerifying(true);

    try {
      const response = await communityAPI.auth.verifyOTP(username, otpCode);

      if (response.data?.user) {
        persistUser(response.data.user);
      }

      // Handle pending profile media uploads right after successful authentication
      let uploadErrors = [];

      const dataUrlToFile = async (dataUrl, fileName) => {
        const res = await fetch(dataUrl);
        const blob = await res.blob();
        return new File([blob], fileName, { type: blob.type });
      };

      if (pendingAvatarDataUrl) {
        try {
          const avatarFile = await dataUrlToFile(pendingAvatarDataUrl, pendingAvatarName || 'avatar.jpg');
          await communityAPI.users.uploadMedia(avatarFile, 'avatar');
        } catch (uploadErr) {
          console.error('Avatar upload failed:', uploadErr);
          uploadErrors.push('Profile picture upload failed.');
        }
      }

      if (pendingCoverDataUrl) {
        try {
          const coverFile = await dataUrlToFile(pendingCoverDataUrl, pendingCoverName || 'cover.jpg');
          await communityAPI.users.uploadMedia(coverFile, 'cover');
        } catch (uploadErr) {
          console.error('Cover upload failed:', uploadErr);
          uploadErrors.push('Cover image upload failed.');
        }
      }

      const baseMessage = 'Email verified successfully! Welcome back.';
      const finalMessage = uploadErrors.length
        ? `${baseMessage} However, some images failed to upload: ${uploadErrors.join(' ')}`
        : baseMessage;

      // Re-fetch user data so auth context has updated avatar/cover URLs
      if (pendingAvatarDataUrl || pendingCoverDataUrl) {
        try {
          const sessionRes = await communityAPI.auth.session();
          if (sessionRes.data?.user) {
            persistUser(sessionRes.data.user);
          }
        } catch (_refreshErr) {
          // Non-critical: profile will refresh on next page load
        }
      }

      navigate('/community', {
        state: { message: finalMessage }
      });
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Verification failed. Please try again.';
      setError(errorMsg);
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    if (resendTimer > 0) return;

    setError('');
    setIsResending(true);

    try {
      await communityAPI.auth.resendOTP(username);
      setResendTimer(60);
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to resend code. Please try again.';
      setError(errorMsg);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-primary/5 via-white to-secondary/5 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
      <div className="absolute inset-0 opacity-40">
        <div className="absolute -top-32 -left-24 h-64 w-64 rounded-full bg-indigo-300 blur-3xl dark:bg-indigo-900/40" />
        <div className="absolute bottom-10 right-10 h-72 w-72 rounded-full bg-purple-200 blur-3xl dark:bg-purple-900/30" />
      </div>

      <div className="relative z-10 flex min-h-screen items-center justify-center px-4 py-16">
        <div className="w-full max-w-md">
          <div className="rounded-3xl border border-white/60 bg-white/90 p-8 shadow-2xl backdrop-blur dark:border-slate-800/60 dark:bg-slate-900/90">
            <div className="text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 text-3xl text-white shadow-lg">
                ✉️
              </div>
              <h2 className="mt-6 text-3xl font-semibold text-gray-900 dark:text-white">
                Verify your email
              </h2>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                {fromLogin ? 'Please verify your email to continue. We sent a 6-digit code to' : 'We sent a 6-digit code to'}
              </p>
              <p className="mt-1 text-sm font-semibold text-primary dark:text-primary/70">
                {email}
              </p>
            </div>

            {error && (
              <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 shadow-sm dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300">
                {error}
              </div>
            )}

            <form onSubmit={handleVerify} className="mt-8">
              <div className="flex justify-center gap-2">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (inputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                    className="h-14 w-12 rounded-xl border-2 border-gray-300 bg-white text-center text-2xl font-bold text-gray-900 shadow-sm transition focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:focus:border-primary/60 dark:focus:ring-primary/20"
                    disabled={isVerifying}
                    autoFocus={index === 0}
                  />
                ))}
              </div>

              <button
                type="submit"
                disabled={isVerifying || otp.join('').length !== 6}
                className="mt-8 w-full rounded-2xl bg-gradient-to-r from-primary to-secondary py-3 text-sm font-semibold text-white shadow-xl transition hover:from-indigo-500 hover:to-purple-500 focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isVerifying ? (
                  <InlineSpinner
                    tone="slate"
                    message="Verifying…"
                    messageClassName="text-white"
                  />
                ) : (
                  'Verify Email'
                )}
              </button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Didn't receive the code?
              </p>
              <button
                type="button"
                onClick={handleResend}
                disabled={isResending || resendTimer > 0}
                className="mt-2 text-sm font-semibold text-primary hover:text-indigo-500 disabled:cursor-not-allowed disabled:opacity-60 dark:text-primary/70"
              >
                {isResending ? (
                  'Sending…'
                ) : resendTimer > 0 ? (
                  `Resend in ${resendTimer}s`
                ) : (
                  'Resend code'
                )}
              </button>
            </div>

            <div className="mt-8 text-center text-sm text-gray-600 dark:text-gray-400">
              <Link
                to={fromLogin ? "/community/login" : "/community/register"}
                className="font-semibold text-primary hover:text-indigo-500 dark:text-primary/70"
              >
                {fromLogin ? 'Back to login' : 'Back to registration'}
              </Link>
            </div>
          </div>

          <div className="mt-6 rounded-2xl border border-primary/30 bg-primary/10/70 p-4 text-center text-sm text-gray-700 dark:border-primary/40 dark:bg-[#3db8d9]/10 dark:text-gray-300">
            <p className="font-semibold">⏱️ Code expires in 10 minutes</p>
            <p className="mt-1 text-xs">You have 3 attempts to enter the correct code</p>
            {fromLogin && (
              <p className="mt-2 text-xs text-primary dark:text-primary/70">
                💡 If you don't have a recent code, click "Resend code" to get a new one
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
